import config from '../config/environment';
import supabase from '../config/supabase';
import { handleServiceError } from '../utils/errorHandler';
import type { PaginatedResponse } from '../types';
import type { PaymentWebhookFailure, PaymentWebhookFailureFilters } from '../types/payment';

class PaymentWebhookFailureServiceClass {
  private async getToken(): Promise<string> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token?.trim();

    if (!token) {
      handleServiceError(new Error('Usuária não autenticada.'), 'acessar falhas de webhook');
    }

    return token as string;
  }

  async listPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters: PaymentWebhookFailureFilters = {},
  ): Promise<PaginatedResponse<PaymentWebhookFailure>> {
    const token = await this.getToken();
    const url = new URL(`${config.supabaseFunctionsUrl}/payment-webhook-recovery`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('pageSize', String(pageSize));
    if (filters.search) url.searchParams.set('search', filters.search);
    if (filters.paymentId) url.searchParams.set('paymentId', filters.paymentId);
    if (filters.provider) url.searchParams.set('provider', filters.provider);
    if (filters.status) url.searchParams.set('status', filters.status);
    if (filters.dateFrom) url.searchParams.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) url.searchParams.set('dateTo', filters.dateTo);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error(err?.error || 'Erro ao listar falhas de webhook'),
        'listar falhas de webhook',
      );
    }

    return response.json() as Promise<PaginatedResponse<PaymentWebhookFailure>>;
  }

  async reprocess(id: number): Promise<void> {
    const token = await this.getToken();
    const response = await fetch(`${config.supabaseFunctionsUrl}/payment-webhook-recovery`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({ action: 'reprocess', id }),
    });

    if (response.status === 422) {
      const err = await response.json().catch(() => ({})) as {
        data?: { action?: string };
        error?: string;
      };
      if (err?.data?.action === 'skipped' || err?.data?.action === 'ignored') {
        return;
      }

      handleServiceError(
        new Error(err?.error || 'Erro ao reprocessar falha de webhook'),
        'reprocessar falha de webhook',
      );
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error(err?.error || 'Erro ao reprocessar falha de webhook'),
        'reprocessar falha de webhook',
      );
    }
  }

  async remove(id: number): Promise<void> {
    await this.postAction(id, 'delete', 'excluir falha de webhook');
  }

  async updatePayload(id: number, payload: string): Promise<void> {
    await this.postAction(id, 'update', 'editar JSON da falha de webhook', { payload });
  }

  private async postAction(
    id: number,
    action: 'reprocess' | 'delete' | 'update',
    context: string,
    extra: Record<string, unknown> = {},
  ): Promise<void> {
    const token = await this.getToken();
    const response = await fetch(`${config.supabaseFunctionsUrl}/payment-webhook-recovery`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({ action, id, ...extra }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error(err?.error || `Erro ao ${context}`),
        context,
      );
    }
  }
}

export const PaymentWebhookFailureService = new PaymentWebhookFailureServiceClass();
